import { useQuery } from '@apollo/client'
import { Blocks } from 'lucide-react'
import { Link, useLocation, useParams } from 'react-router'
import {
  BreadcrumbItem,
  BreadcrumbPage,
  BreadcrumbLink,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from '@/components/ui/tooltip'
import { gql } from '@/lib/graphql/gql'
import { useCompanyPath } from '@/lib/hooks'
import type {
  TemplateBreadcrumbQuery,
  TemplateBreadcrumbQueryVariables,
} from '@/components/layout/breadcrumbs/templates/templates-breadcrumbs.types.gen'

const TemplatesIconBreadcrumbItem = () => {
  const { getCompanyPath } = useCompanyPath()
  return (
    <BreadcrumbItem>
      <Link to={getCompanyPath({ pathname: '/templates' })}>
        <Tooltip delayDuration={1000}>
          <BreadcrumbPage className="cursor-pointer p-2 flex items-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground">
            <TooltipTrigger>
              <BreadcrumbLink asChild>
                <div className="cursor-pointer">
                  <Blocks className="size-3.5" />
                  <span className="sr-only">Templates</span>
                </div>
              </BreadcrumbLink>
            </TooltipTrigger>
          </BreadcrumbPage>
          <TooltipContent sideOffset={8}>
            <span>Templates</span>
          </TooltipContent>
        </Tooltip>
      </Link>
    </BreadcrumbItem>
  )
}

const TEMPLATE_BREADCRUMB_QUERY = gql`
  query TemplateBreadcrumb($name: String!) {
    templateByName(name: $name) {
      id
      displayName
    }
  }
`

export const TemplatesBreadcrumbs = () => {
  const { pathname } = useLocation()
  const { name = '' } = useParams()
  const { data, loading } = useQuery<
    TemplateBreadcrumbQuery,
    TemplateBreadcrumbQueryVariables
  >(TEMPLATE_BREADCRUMB_QUERY, {
    variables: { name },
    fetchPolicy: 'cache-only',
  })

  const template = data?.templateByName

  return (
    <>
      {pathname.startsWith('/templates') ? (
        <BreadcrumbItem>
          <BreadcrumbPage className="flex items-center gap-2 px-2">
            <Blocks className="size-3.5" />
            <span className="font-medium">Templates</span>
          </BreadcrumbPage>
        </BreadcrumbItem>
      ) : null}
      {pathname.startsWith('/template/') ? (
        <>
          <TemplatesIconBreadcrumbItem />
          <BreadcrumbSeparator />
          {!loading && template ? (
            <BreadcrumbItem>
              <BreadcrumbPage className="flex items-center gap-2 px-2 font-medium">
                <span>{template.displayName}</span>
              </BreadcrumbPage>
            </BreadcrumbItem>
          ) : null}
        </>
      ) : null}
    </>
  )
}
